import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from './api';
import useAuth from '../context/AuthProvider';

interface ExtractionState {
    name: string;
    aliases: string[];
};

/** Fetches the list of live extraction states from the API and returns helper functions built from
 *  that list. Add a new state on the backend only — nothing here needs to change. */
export function useExtractionStates() {
    const { user } = useAuth();
    const [states, setStates] = useState<ExtractionState[]>([]);

    useEffect(() => {
        if (!user?.token) return;
        apiClient('/AutomaticFiling/getExtractionStates', {
            headers: { Authorization: `Bearer ${user.token}` },
        }).then((res: any) => {
            if (Array.isArray(res?.data)) setStates(res.data);
        });
    }, [user?.token]);

    const segmentMap = useMemo(() => {
        const map: Record<string, string> = {};
        for (const s of states) {
            map[s.name.toLowerCase()] = s.name;
            for (const alias of s.aliases) {
                map[alias.toLowerCase()] = s.name;
            }
        }
        return map;
    }, [states]);

    // MEMOIZED, not a plain function expression. `segmentMap` is already memoized on the fetched state
    // list, but a bare arrow re-declared here handed every caller a BRAND-NEW function identity on each
    // render. BatchFilingReview lists `stateSegment` in the dependency array of its `columns` useMemo, so
    // that memo was invalidated on literally every render — the grid re-derived its whole column set (and
    // every renderCell closure with it) even when nothing about the states, the rows or the columns had
    // changed. Keyed on `segmentMap` so the identity still changes exactly when the lookup it reads does.
    const stateSegment = useCallback(
        (raw?: string | null): string | null =>
            raw ? segmentMap[raw.trim().toLowerCase()] ?? null : null,
        [segmentMap],
    );

    // Memoized for the same reason and with the real dependency kept, not silenced: it is derived from
    // `stateSegment`, so it is stable exactly as long as `stateSegment` is.
    const isFilingReviewSupportedState = useCallback(
        (raw?: string | null): boolean => stateSegment(raw) !== null,
        [stateSegment],
    );

    return { stateSegment, isFilingReviewSupportedState, states };
};
